import { getLocale } from '@/lib/locale';

export const AI_VIDEO_EXTENDER_PATH = '/ai-video-extender';
export const AI_VIDEO_EXTENDER_DATE_PUBLISHED = '2026-07-23';
export const AI_VIDEO_EXTENDER_LAST_UPDATED = '2026-07-24';
export const AI_VIDEO_EXTENDER_LAST_UPDATED_LABEL = 'July 24, 2026';
export const AI_VIDEO_EXTENDER_SEO_TITLE =
  'AI Video Extender: Extend Video Length by 6 or 10 Seconds';
export const AI_VIDEO_EXTENDER_SEO_DESCRIPTION =
  'Use AI Video Extender online to upload MP4, WebM, or MOV up to 100MB, or choose a Grok History clip. Extend video length by 6 or 10 seconds.';
export const AI_VIDEO_EXTENDER_SOURCE_URL =
  'https://docs.kie.ai/market/grok-imagine/extend';
export const AI_VIDEO_EXTENDER_KEYWORDS = [
  'ai video extender',
  'video extender ai',
  'ai video extender free',
  'ai extend video length free',
  'extend video ai',
  'ai extend video',
  'free ai video extender',
  'extend video with ai',
  'ai video extender free online',
  'video outpainting ai',
  'make a video longer',
].join(', ');

export const AI_VIDEO_EXTENDER_QUICK_ANSWER =
  'An AI video extender generates new frames after the final frame of an existing video. Uploads create a separate Grok Imagine 1.5 video segment from the last frame; eligible History clips use the provider native extend task.';

export const AI_VIDEO_EXTENDER_TOOL_DETAILS = [
  {
    label: 'Video input',
    value:
      'Upload a video or choose an eligible completed Grok clip from History',
  },
  {
    label: 'Upload formats',
    value: 'MP4, WebM, or MOV; maximum file size 100MB',
  },
  {
    label: 'Added duration',
    value: '6 or 10 seconds after the original ending',
  },
  {
    label: 'Upload output',
    value: 'A separate 480p video segment generated with Grok Imagine 1.5',
  },
  {
    label: 'Current upload estimate',
    value: '50 credits for 6 seconds; 80 credits for 10 seconds',
  },
  {
    label: 'History estimate',
    value: 'Shown live; model, resolution, and audio can change it',
  },
  {
    label: 'History requirement',
    value: 'A completed Grok clip with its original provider task ID',
  },
  { label: 'Last updated', value: AI_VIDEO_EXTENDER_LAST_UPDATED_LABEL },
];

export const AI_VIDEO_EXTENDER_FEATURES = [
  {
    title: 'Upload or choose a clip',
    body: 'Start with an MP4, WebM, or MOV from your camera roll, or choose a completed Grok clip from History. Upload mode reads the final frame; History mode preserves the original Kie task ID for native extension.',
  },
  {
    title: 'Describe one next action',
    body: 'Write what happens after the visible ending: the car keeps drifting, the person turns toward camera, the bottle stays in a hero hold, or the camera reveals the wider landscape. A concrete next beat is more useful than repeating the entire original prompt.',
  },
  {
    title: 'Choose the missing edit time',
    body: 'Use +6 seconds for one reaction, gesture, reveal, or transition. Use +10 seconds when the action needs to resolve or the ad needs room for a CTA. The selected duration is visible before you submit.',
  },
  {
    title: 'Review motion and identity at the join',
    body: 'Compare the original video and extended result side by side. Check car speed, body position, face and hands, product edges, reflections, horizon lines, and ambient sound before downloading the result.',
  },
  {
    title: 'Download the added footage',
    body: 'The generated segment is saved to your account after the async job completes. A Grok Imagine 1.5 or uploaded result is a new segment to place after the original video. A classic Grok Imagine native extension stays connected to its History record.',
  },
];

export const AI_VIDEO_EXTENDER_CONTROLS = [
  {
    control: 'Input video',
    options: 'Upload MP4/WebM/MOV or choose an eligible History clip',
    check:
      'Use upload for filmed or exported footage; use History for native Grok extension.',
  },
  {
    control: 'Next-scene prompt',
    options: 'Subject, next action, camera movement, setting, mood',
    check:
      'Write forward from the final moment; do not repeat the whole original prompt.',
  },
  {
    control: 'Added length',
    options: '+6 seconds or +10 seconds',
    check:
      'Use 6 seconds for one beat; use 10 seconds for a fuller action or CTA hold.',
  },
  {
    control: 'Motion direction',
    options: 'Forward extension from the final frame',
    check:
      'Describe the next camera or subject movement in the same direction where possible.',
  },
  {
    control: 'Format',
    options: 'History keeps original settings; upload uses Grok 1.5 at 480p',
    check:
      'Review framing and safe areas in the original and extended result together.',
  },
  {
    control: 'Audio and continuity',
    options: 'Provider-generated audio and motion',
    check:
      'Listen for changes in ambience or dialogue before using the result in a final edit.',
  },
];

export const AI_VIDEO_EXTENDER_STEPS = [
  {
    name: 'Upload or choose a clip',
    text: 'Upload an MP4, WebM, or MOV up to 100MB, or switch to History and choose an eligible completed Grok clip. The uploaded file stays in the workbench draft across login.',
  },
  {
    name: 'Describe what happens next',
    text: 'Write a prompt that begins at the original ending. Explain the next movement or camera beat instead of repeating the entire first scene.',
  },
  {
    name: 'Choose the added seconds',
    text: 'Select 6 seconds for one missing beat or 10 seconds for a fuller action and check the displayed credit estimate before generation.',
  },
  {
    name: 'Generate, compare, and download',
    text: 'History clips use native Grok extension. Uploaded clips generate new footage from the extracted last frame. Compare the original and result, then download after checking the join.',
  },
];

export const AI_VIDEO_EXTENDER_VIDEO_EXAMPLES = [
  {
    title: 'Carry high-speed racing motion through the cut',
    job: 'Extend a motorsport shot while two cars are still drifting on a wet circuit.',
    outcome:
      'The shot progresses from a close track-level drift into smoke, separation, and a wider aerial view, giving an editor more speed ramp and transition options.',
    review:
      'Watch wheel direction, relative car position, spray and smoke velocity, track geometry, and whether the camera change feels intentional.',
    videoUrl: '/ai-video-extender/examples/racing-motion-continuation.mp4',
    posterUrl: '/ai-video-extender/examples/racing-motion-continuation.jpg',
    captionUrl: '/ai-video-extender/examples/racing-motion-continuation.vtt',
    duration: 'PT16.489S',
    durationLabel: '16.5 seconds',
  },
  {
    title: 'Continue a character reveal across a reflective landscape',
    job: 'Give a salt-lake fashion shot time to move from an extreme wide view to a recognizable close-up.',
    outcome:
      'The extended shot preserves the red-and-white subject cue while the camera closes distance and the reflected sky remains part of the composition.',
    review:
      'Check face and hair identity, wardrobe color, horizon stability, reflections, ripples, and the scale change from the wide shot.',
    videoUrl: '/ai-video-extender/examples/salt-lake-scene-continuation.mp4',
    posterUrl: '/ai-video-extender/examples/salt-lake-scene-continuation.jpg',
    captionUrl: '/ai-video-extender/examples/salt-lake-scene-continuation.vtt',
    duration: 'PT15.069S',
    durationLabel: '15.1 seconds',
  },
  {
    title: 'Finish a perfume action and create an ad-ready exit',
    job: 'Lengthen a close perfume spray so the action resolves and the character can leave the room instead of cutting immediately after the product beat.',
    outcome:
      'The clip gains a complete spray, reaction, and walk-away sequence with additional room for voiceover, a CTA, or an end card.',
    review:
      'Check the bottle and hand, clothing continuity, spray timing, doorway geometry, and whether the final walk-away gives enough clean edit room.',
    videoUrl: '/ai-video-extender/examples/perfume-ad-length-extension.mp4',
    posterUrl: '/ai-video-extender/examples/perfume-ad-length-extension.jpg',
    captionUrl: '/ai-video-extender/examples/perfume-ad-length-extension.vtt',
    duration: 'PT14.91S',
    durationLabel: '14.9 seconds',
  },
];

export const AI_VIDEO_EXTENDER_PROMPT_EXAMPLES = [
  {
    title: 'Person action',
    prompt:
      'The subject opens the studio door and steps into the warm light beyond it, with the camera following in one smooth movement.',
    settings: '6 seconds · match the existing camera direction',
    result: 'A resolved action beat instead of a clip that stops at the setup.',
  },
  {
    title: 'Camera movement',
    prompt:
      'The camera keeps orbiting to the right while the character turns toward the window; preserve the same pace, lighting, and composition.',
    settings: '6 seconds · keep motion continuous',
    result: 'A motion bridge that can connect to the next shot.',
  },
  {
    title: 'Product advertisement',
    prompt:
      'Continue the slow pullback and hold on the product as the light sweeps across its surface, leaving clean space for a short CTA.',
    settings: '10 seconds · review logo and safe area',
    result: 'Extra room for an end card, caption, or voiceover.',
  },
  {
    title: 'Environment reveal',
    prompt:
      'The camera moves past the foreground leaves to reveal the wider landscape, keeping the same golden-hour color and gentle wind.',
    settings: '10 seconds · check background geometry',
    result: 'A longer establishing beat without recreating the opening shot.',
  },
  {
    title: 'Quiet outro',
    prompt:
      'Let the subject settle into a natural pause, keep the ambient movement subtle, and hold the final composition for a clean fade out.',
    settings: '6 seconds · inspect audio and stillness',
    result:
      'A calmer ending for a short film, social post, or presentation clip.',
  },
];

export const AI_VIDEO_EXTENDER_USE_CASES = [
  {
    title: 'Finish an action that stops mid-motion',
    body: 'The extender can finish a shot that ends while a car is drifting, a person is turning, a door is opening, or a camera is still pushing forward. Request one next action, then inspect speed and body position across the join.',
  },
  {
    title: 'Cover the rest of a voiceover or caption',
    body: 'A social edit may end before its spoken line, subtitle, or hook is complete. A controlled hold, slow pullback, or ambient movement can keep the frame active while the remaining message finishes.',
  },
  {
    title: '15/30-second advertising edits',
    body: 'A perfume spray or product reveal may land correctly but leave no room for a CTA. AI video extension can complete the gesture, settle into a product hold, or create an exit while preserving copy-safe space.',
  },
  {
    title: 'Turn a short establishing shot into a reveal',
    body: 'AI can extend a distant landscape or room view toward a person or object. The added shot creates a wide-to-close progression without discarding the opening composition.',
  },
  {
    title: 'Create a bridge into the next shot',
    body: 'A continued pan, orbit, pullback, walk-away, or vehicle movement can reach a cleaner cut point. The added seconds provide a transition plate; they do not guarantee that every generated join will be invisible.',
  },
];

export const AI_VIDEO_EXTENDER_CONSISTENCY_CHECKS = [
  {
    title: 'End frame clarity',
    body: 'The final moment should have a readable subject, action, and camera direction. Ambiguous endings give the generated scene more room to drift.',
  },
  {
    title: 'One next action',
    body: 'Ask for one primary action in the added seconds. Several unrelated actions can make timing, identity, and camera continuity harder to inspect.',
  },
  {
    title: 'Camera direction',
    body: 'Name the next pan, push, orbit, pullback, or hold. Matching the existing movement is usually easier to review than changing direction without a transition.',
  },
  {
    title: 'Identity and color',
    body: 'Compare face, hands, wardrobe, product details, lighting direction, and color temperature across the join. Generative video can change any of them.',
  },
  {
    title: 'Audio review',
    body: 'Listen for ambience, dialogue, music, and abrupt changes. The extender does not promise a frame-perfect or audio-perfect match.',
  },
];

export const AI_VIDEO_EXTENDER_COMPARISON = [
  {
    need: 'Continue the same generated scene',
    extender:
      'Use a completed classic Grok Imagine clip for native extension. Grok Imagine 1.5 continues from its final frame and your prompt.',
    alternative: 'Best fit for this page.',
  },
  {
    need: 'Repeat a segment in a loop',
    extender: 'Creates new content, not a frame-perfect loop.',
    alternative: 'Use a video editor or loop tool.',
  },
  {
    need: 'Make the existing clip play slower',
    extender: 'Adds generated content instead of changing playback speed.',
    alternative: 'Use a speed or retiming tool.',
  },
  {
    need: 'Change an object or style in the original video',
    extender: 'Does not replace the video edit workflow.',
    alternative: 'Use Video Editor, then extend the saved result.',
  },
  {
    need: 'Extend an arbitrary uploaded MP4',
    extender: 'Upload the clip and generate a new segment from its last frame.',
    alternative:
      'Join the original video and added segment in your timeline after review.',
  },
];

export const AI_VIDEO_EXTENDER_FAQS = [
  {
    question: 'What is an AI video extender?',
    answer:
      'An AI video extender generates new frames after the final frame of an existing video. It creates new visual content rather than slowing, duplicating, or looping existing frames. Classic Grok Imagine History clips use native extension. Grok Imagine 1.5 and uploaded videos start from their extracted last frame.',
  },
  {
    question: 'Which video formats and file sizes are supported?',
    answer:
      'AI Video Extender accepts MP4, WebM, and MOV uploads up to 100MB. The browser reads the uploaded video and extracts its final frame before generation. A completed Grok clip can also be selected from History when its original provider task ID is available.',
  },
  {
    question: 'Which model extends uploaded and History videos?',
    answer:
      'Uploaded videos and Grok Imagine 1.5 History clips use the extracted final frame as the start image for Grok Imagine 1.5 image-to-video generation. The prompt you write describes the next action. Classic Grok Imagine History clips use Kie native grok-imagine/extend with the original provider task ID. A 1.5 or uploaded result is a separate segment to join after the original footage in an editor.',
  },
  {
    question: 'Can I use AI Video Extender free online?',
    answer:
      'The workbench can be opened and configured online, but this is not a free AI video extender for completed generations. As of July 23, 2026, uploaded-video estimates are 50 credits for 6 seconds and 80 credits for 10 seconds. History cost depends on the selected clip settings and is shown before generation.',
  },
  {
    question: 'Will the character, camera, and audio stay consistent?',
    answer:
      'AI video extension cannot guarantee an exact identity, motion, lighting, or audio match. Use a clip with a clear final frame, request one next action, preserve the existing camera direction, and compare faces, hands, product details, ambience, and movement across the join before publishing.',
  },
  {
    question: 'Can this make a video longer?',
    answer:
      'Yes. Making a video longer here means generating the next 6 or 10 seconds after the last frame. It does not stretch or slow the frames you already have. If you only have a still photo, start with Photo to Animation, then extend that clip.',
  },
];

export const AI_VIDEO_EXTENDER_SCHEMA_FEATURES = AI_VIDEO_EXTENDER_FEATURES.map(
  (feature) => feature.title
);

export function getAiVideoExtenderSeoData() {
  return {
    path: AI_VIDEO_EXTENDER_PATH,
    seoTitle: AI_VIDEO_EXTENDER_SEO_TITLE,
    seoDescription: AI_VIDEO_EXTENDER_SEO_DESCRIPTION,
    keywords: AI_VIDEO_EXTENDER_KEYWORDS,
    sourceUrl: AI_VIDEO_EXTENDER_SOURCE_URL,
    quickAnswer: AI_VIDEO_EXTENDER_QUICK_ANSWER,
    toolDetails: AI_VIDEO_EXTENDER_TOOL_DETAILS,
    features: AI_VIDEO_EXTENDER_FEATURES,
    controls: AI_VIDEO_EXTENDER_CONTROLS,
    steps: AI_VIDEO_EXTENDER_STEPS,
    videoExamples: AI_VIDEO_EXTENDER_VIDEO_EXAMPLES,
    examples: AI_VIDEO_EXTENDER_PROMPT_EXAMPLES,
    useCases: AI_VIDEO_EXTENDER_USE_CASES,
    consistencyChecks: AI_VIDEO_EXTENDER_CONSISTENCY_CHECKS,
    comparison: AI_VIDEO_EXTENDER_COMPARISON,
    faqs: AI_VIDEO_EXTENDER_FAQS,
    schemaFeatures: AI_VIDEO_EXTENDER_SCHEMA_FEATURES,
    datePublished: AI_VIDEO_EXTENDER_DATE_PUBLISHED,
    lastUpdated: AI_VIDEO_EXTENDER_LAST_UPDATED,
    locale: getLocale(),
  };
}
